import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, AlertCircle, Copy as CopyIcon, MessageCircle, Send } from "lucide-react";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    style: user?.style || "informal",
    region: user?.region || "",
    property_focus: user?.property_focus || "",
    reminder_hour: user?.reminder_hour || 9,
    search_website_url: user?.search_website_url || "",
    search_city: user?.search_city || "",
    search_uf: user?.search_uf || "",
  });
  const [saving, setSaving] = useState(false);
  const [waInfo, setWaInfo] = useState(null);
  const [waTesting, setWaTesting] = useState(false);
  const [briefTesting, setBriefTesting] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/info").then((r) => setWaInfo(r.data)).catch(() => {});
  }, []);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copiado!"); } catch { toast.error("Não consegui copiar"); }
  };

  const testWhatsApp = async () => {
    setWaTesting(true);
    try {
      await api.post("/whatsapp/test-send");
      toast.success("Mensagem enviada! Verifique seu WhatsApp.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally { setWaTesting(false); }
  };

  const testDailyBrief = async () => {
    setBriefTesting(true);
    try {
      await api.post("/whatsapp/send-daily-brief");
      toast.success("Briefing enviado pro seu WhatsApp!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally { setBriefTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", form);
      await refreshUser();
      toast.success("Configurações salvas!");
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-3xl mx-auto" data-testid="settings-root">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4651A] font-bold mb-1">Personalização</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C19]">Configurações</h1>
        <p className="text-stone-500 mt-1 text-sm">Como o estatebroker.AI deve te tratar</p>
      </div>

      <Card className="p-6 rounded-2xl border-stone-200 space-y-5">
        <div>
          <Label className="text-sm font-semibold">Seu nome</Label>
          <Input
            data-testid="settings-name-input"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="mt-1.5"
          />
          <p className="text-xs text-stone-500 mt-1">É como o assistente vai te chamar.</p>
        </div>

        <div>
          <Label className="text-sm font-semibold">Estilo de comunicação</Label>
          <Select value={form.style} onValueChange={(v) => setForm({...form, style: v})}>
            <SelectTrigger data-testid="settings-style-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="informal">Informal · próximo, descontraído</SelectItem>
              <SelectItem value="formal">Formal · profissional e calmo</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-stone-500 mt-1">Afeta o tom das copys e do assistente.</p>
        </div>

        <div>
          <Label className="text-sm font-semibold">Sua região de atuação</Label>
          <Input
            data-testid="settings-region-input"
            value={form.region}
            onChange={(e) => setForm({...form, region: e.target.value})}
            className="mt-1.5"
            placeholder="Ex.: Zona Sul de SP, Recife, Curitiba..."
          />
        </div>

        <div>
          <Label className="text-sm font-semibold">Tipo de imóvel que mais trabalha</Label>
          <Input
            data-testid="settings-focus-input"
            value={form.property_focus}
            onChange={(e) => setForm({...form, property_focus: e.target.value})}
            className="mt-1.5"
            placeholder="Ex.: lançamentos, alto padrão, MCMV..."
          />
        </div>

        <div>
          <Label className="text-sm font-semibold">Horário do resumo diário</Label>
          <Select value={String(form.reminder_hour)} onValueChange={(v) => setForm({...form, reminder_hour: parseInt(v)})}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[7,8,9,10,11,12].map((h) => <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-stone-500 mt-1">Quando o assistente vai te mandar o resumo do dia.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold">Cidade principal de atuação</Label>
            <Input
              data-testid="settings-search-city-input"
              value={form.search_city}
              onChange={(e) => setForm({...form, search_city: e.target.value})}
              className="mt-1.5"
              placeholder="Ex.: Florianópolis"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold">Estado (UF)</Label>
            <Input
              data-testid="settings-search-uf-input"
              value={form.search_uf}
              onChange={(e) => setForm({...form, search_uf: e.target.value.toLowerCase().slice(0, 2)})}
              className="mt-1.5"
              placeholder="Ex.: sc"
              maxLength={2}
            />
          </div>
        </div>
        <p className="text-xs text-stone-500 -mt-2">
          Uso a cidade e o estado pra montar as buscas de imóveis na ordem certa: primeiro o seu site,
          depois os portais, depois a busca aberta.
        </p>

        <div>
          <Label className="text-sm font-semibold">Site de busca de imóveis (opcional)</Label>
          <Input
            data-testid="settings-search-website-input"
            value={form.search_website_url}
            onChange={(e) => setForm({...form, search_website_url: e.target.value})}
            className="mt-1.5"
            placeholder="Ex.: https://donnascimoveis.com.br"
            type="url"
          />
          <p className="text-xs text-stone-500 mt-1">
            Se você tem um site que usa pra pesquisar sua pauta (o da sua imobiliária ou o seu próprio), cola aqui.
            Quando você pedir busca via WhatsApp, eu uso esse site como referência antes de partir pro Zap/OLX.
          </p>
        </div>

        <Button
          data-testid="settings-save-btn"
          onClick={save} disabled={saving}
          className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white w-full sm:w-auto"
        >
          {saving ? "Salvando..." : "Salvar configurações"}
        </Button>
      </Card>

      {/* Account */}
      <Card className="p-6 rounded-2xl border-stone-200 mt-5">
        <h2 className="font-heading text-lg font-bold mb-3">Sua conta</h2>
        <div className="space-y-2 text-sm">
          <Row label="E-mail" value={user?.email} />
          <Row label="WhatsApp" value={user?.phone} />
          <Row label="Plano atual" value={<span className="capitalize">{user?.plan}</span>} />
        </div>
      </Card>

      {/* WhatsApp Setup */}
      <Card className="p-6 rounded-2xl border-stone-200 mt-5" data-testid="whatsapp-setup-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#1B3A2F]" />
              WhatsApp · Conectar
            </h2>
            <p className="text-sm text-stone-500 mt-0.5">Converse com o estatebroker.AI direto pelo WhatsApp</p>
          </div>
          {waInfo?.configured ? (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              <Check className="w-3 h-3 mr-1" /> Pronto
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
              <AlertCircle className="w-3 h-3 mr-1" /> Aguardando credenciais
            </Badge>
          )}
        </div>

        {!waInfo?.configured ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-stone-700 space-y-2">
            <p className="font-semibold text-amber-900">Configuração pendente do servidor</p>
            <p>O administrador precisa configurar as credenciais Twilio em <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">backend/.env</code>:</p>
            <ul className="list-disc list-inside font-mono text-xs space-y-0.5 mt-2">
              <li>TWILIO_ACCOUNT_SID</li>
              <li>TWILIO_AUTH_TOKEN</li>
              <li>TWILIO_SANDBOX_JOIN_CODE</li>
            </ul>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Steps */}
            <div className="space-y-3">
              <Step n={1} title="Salve o número do estatebroker.AI na sua agenda">
                <div className="flex items-center gap-2 mt-1.5">
                  <code className="bg-stone-100 px-3 py-2 rounded-lg font-mono text-sm flex-1" data-testid="wa-number">
                    {waInfo.whatsapp_number}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copy(waInfo.whatsapp_number)} data-testid="wa-copy-number">
                    <CopyIcon className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </Step>

              {waInfo.join_code && (
                <Step n={2} title='Envie a frase de ativação por WhatsApp para esse número'>
                  <div className="flex items-center gap-2 mt-1.5">
                    <code className="bg-stone-100 px-3 py-2 rounded-lg font-mono text-sm flex-1" data-testid="wa-join-code">
                      join {waInfo.join_code}
                    </code>
                    <Button size="sm" variant="outline" onClick={() => copy(`join ${waInfo.join_code}`)} data-testid="wa-copy-join">
                      <CopyIcon className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <p className="text-xs text-stone-500 mt-1.5">
                    Só precisa fazer isso uma vez. Após a confirmação, o estatebroker.AI te atende automaticamente.
                  </p>
                </Step>
              )}

              <Step n={waInfo.join_code ? 3 : 2} title="Confira se seu número está cadastrado">
                <div className="mt-1.5 text-sm">
                  Seu WhatsApp cadastrado: <strong className="font-mono">{user?.phone}</strong>
                  <span className="text-xs text-stone-500 block mt-1">
                    Toda mensagem que vier desse número será associada à sua conta automaticamente.
                  </span>
                </div>
              </Step>

              <Step n={waInfo.join_code ? 4 : 3} title="Teste o envio">
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={testWhatsApp}
                    disabled={waTesting}
                    data-testid="wa-test-send-btn"
                    className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white"
                  >
                    <Send className="w-3.5 h-3.5 mr-2" />
                    {waTesting ? "Enviando..." : "Mandar mensagem teste"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={testDailyBrief}
                    disabled={briefTesting}
                    data-testid="wa-brief-send-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-2" />
                    {briefTesting ? "Enviando..." : "Enviar briefing do dia"}
                  </Button>
                </div>
              </Step>
            </div>

            {waInfo.webhook_url && (
              <details className="text-xs text-stone-500 mt-4 border-t border-stone-100 pt-3">
                <summary className="cursor-pointer font-medium">Detalhes técnicos (admin)</summary>
                <div className="mt-2 space-y-1 font-mono">
                  <div>Webhook URL: <code className="bg-stone-50 px-1.5 py-0.5 rounded break-all">{waInfo.webhook_url}</code></div>
                  <div>Configure no Twilio Console em Messaging, Sandbox settings, campo &ldquo;When a message comes in&rdquo; (HTTP POST)</div>
                </div>
              </details>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-stone-100 last:border-0 py-2">
      <span className="text-stone-500">{label}</span>
      <span className="font-medium text-stone-800">{value}</span>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1B3A2F] text-white text-xs font-bold flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-stone-800">{title}</div>
        {children}
      </div>
    </div>
  );
}
